import React from "react";
import { View, StyleSheet } from "react-native";
import { colors, layout } from "@/constants";
import Interactive from "./Interactive";
import Text from "./Text";
import Icon from "./Icon";

/**
 * Props for the Checkbox component
 */
interface CheckboxProps {
  /** Whether the checkbox is currently checked */
  checked: boolean;
  /** Text label to display next to the checkbox */
  label: string;
  /** Optional text style overrides for the label */
  labelStyle?: object;
  /** Whether the checkbox is disabled */
  disabled?: boolean;
  /** Callback function called when the checkbox is toggled */
  onToggle: () => void;
}

/**
 * A reusable checkbox component with a clickable label.
 * Provides consistent styling and behavior across the app.
 *
 * @param props - The component props
 * @returns An interactive checkbox row with label
 */
export default function Checkbox({
  checked,
  onToggle,
  label,
  labelStyle,
  disabled = false
}: CheckboxProps) {
  return (
    <Interactive
      onPress={onToggle}
      style={styles.container}
      disabled={disabled}
      accessibilityRole="checkbox"
      accessibilityState={{ checked, disabled }}
    >
      <View style={[styles.checkbox, checked && styles.checkboxChecked]}>
        {checked && (
          <Icon name="check" size="small" style={{ color: colors.onPrimary }} />
        )}
      </View>

      <Text style={[styles.label, labelStyle]}>{label}</Text>
    </Interactive>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: layout.space.md,
    paddingVertical: layout.space.sm,
    paddingHorizontal: layout.space.md,
    borderRadius: layout.borderRadius.medium
  },
  checkbox: {
    width: layout.space.xl,
    height: layout.space.xl,
    borderWidth: layout.borderWidth.thick,
    borderColor: colors.outline,
    borderRadius: layout.borderRadius.small,
    backgroundColor: colors.surface,
    justifyContent: "center",
    alignItems: "center",
    marginTop: 2,
    flexShrink: 0
  },
  checkboxChecked: {
    backgroundColor: colors.primary,
    borderColor: colors.primary
  },
  label: {
    flex: 1
  }
});
